import { apiClient } from './axios';

/** Limitele impuse de backend (auth-service): PNG/JPEG, max 2 MB. */
export const SIGNATURE_MAX_BYTES = 2 * 1024 * 1024;
export const SIGNATURE_ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg'];
export const PARAFA_CODE_MAX_LENGTH = 32;

export interface OperatorRole {
  id: string;
  name: string;
  displayName: string;
}

export interface OperatorProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: OperatorRole;
  parafaCode?: string | null;
  signatureFileId?: string | null;
}

export interface SignatureUploadResult {
  signatureFileId: string;
  fileName: string;
  sizeBytes: number;
}

const unwrapResponse = <T>(responseData: unknown): T => {
  const payload = responseData as { data?: T } | T | undefined;
  if (payload && typeof payload === 'object' && 'data' in payload && (payload as { data?: unknown }).data !== undefined) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

export const operatorProfileApi = {
  /** GET /auth/me → utilizatorul curent (sau { user } împachetat). */
  async getProfile(): Promise<OperatorProfile> {
    const response = await apiClient.get('/auth/me');
    const payload = unwrapResponse<{ user?: OperatorProfile } & OperatorProfile>(response.data);
    return (payload?.user ?? payload) as OperatorProfile;
  },

  /** PUT /auth/me/signature — multipart, câmpul `file`. */
  async uploadSignature(file: File): Promise<SignatureUploadResult> {
    const body = new FormData();
    body.append('file', file);
    // Content-Type se anulează: cu `application/json` axios ar serializa FormData
    // ca JSON și semnătura (fișierul) s-ar pierde. Browserul pune boundary-ul.
    const response = await apiClient.put('/auth/me/signature', body, {
      headers: { 'Content-Type': undefined },
    });
    return unwrapResponse<SignatureUploadResult>(response.data);
  },

  /** GET /auth/me/signature — imaginea semnăturii (404 dacă nu există). */
  async getSignature(): Promise<Blob> {
    const response = await apiClient.get('/auth/me/signature', { responseType: 'blob' });
    return response.data as Blob;
  },

  /** PATCH /auth/me — acceptă doar `parafaCode` (rol/status/email nu se trimit). */
  async updateParafaCode(parafaCode: string): Promise<OperatorProfile> {
    const response = await apiClient.patch('/auth/me', { parafaCode });
    return unwrapResponse<OperatorProfile>(response.data);
  },
};

export default operatorProfileApi;
